import {
  Button,
  DatePicker,
  Empty,
  Input,
  Select,
  Space,
  Spin,
  Switch,
  Typography,
} from 'antd';
import { BasePageComponent } from 'components';
import dayjs, { Dayjs } from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useVendorSeasonListContents } from 'hooks/useVendorSeasonListContents';
import {
  buildSeasonItems,
  countDownloadableFiles,
  EpisodeDownloadTable,
} from '../components/EpisodeDownloadTable';
import { startBatchDownload } from '../download/streamDownload';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// CP 이름이 벤더에 노출되면 안 되므로 필터 옵션 라벨도 그냥 숫자로 표시.
const SOURCE_OPTIONS = [
  { value: 1, label: '1' },
  { value: 2, label: '2' },
  { value: 3, label: '3' },
];

const ORDERING_OPTIONS = [
  { value: '-episode_order', label: 'EP desc' },
  { value: 'episode_order', label: 'EP asc' },
  { value: '-file_uploaded_at', label: 'Newest upload' },
  { value: 'file_uploaded_at', label: 'Oldest upload' },
];

const SEARCH_DEBOUNCE_MS = 300;
const DEFAULT_PAGE_SIZE = 20;
const PAGE_SIZE_OPTIONS = ['10', '20', '50', '100'];

export const VendorDownloadSeasonPage: React.FC = () => {
  const { seasonId } = useParams<{ seasonId: string }>();
  const navigate = useNavigate();
  const sid = Number(seasonId);

  const [ingestSource, setIngestSource] = useState<number | undefined>(undefined);
  const [uploadedRange, setUploadedRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [hasOrigin, setHasOrigin] = useState<boolean>(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [ordering, setOrdering] = useState<string>('-episode_order');
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  // 검색 입력 디바운스 (300ms). 매 키스트로크마다 요청 나가는 것 방지.
  useEffect(() => {
    const t = setTimeout(() => setSearchTerm(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput]);

  const uploadedAfter = uploadedRange?.[0]?.format('YYYY-MM-DD') ?? null;
  const uploadedBefore = uploadedRange?.[1]?.format('YYYY-MM-DD') ?? null;

  // 필터가 바뀌면 페이지 1로 리셋. (다른 페이지에 남아 결과가 안 보이는 상황 방지)
  useEffect(() => {
    setPage(1);
  }, [ingestSource, uploadedAfter, uploadedBefore, hasOrigin, searchTerm, ordering]);

  const { data, isFetching } = useVendorSeasonListContents({
    seasonId: sid,
    page,
    pageSize,
    ingestSource: ingestSource ?? null,
    uploadedAfter,
    uploadedBefore,
    hasOrigin: hasOrigin ? true : null,
    search: searchTerm || null,
    ordering,
  });

  const rows = data?.results ?? [];
  const totalCount = data?.count ?? 0;
  const headerTitle = rows[0]?.season_title || `Season #${sid}`;

  const totalFiles = useMemo(
    () => rows.reduce((sum, row) => sum + countDownloadableFiles(row), 0),
    [rows]
  );

  return (
    <BasePageComponent>
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <Space style={{ justifyContent: 'space-between', width: '100%' }}>
          <Space>
            <Button onClick={() => navigate('/vendor/download')}>Back</Button>
            <Title level={4} style={{ margin: 0 }}>
              {headerTitle}
            </Title>
          </Space>
          <Button
            type="primary"
            disabled={totalFiles === 0 || isFetching}
            onClick={() => startBatchDownload(buildSeasonItems(rows))}
          >
            Download all{totalFiles > 0 ? ` (${totalFiles} files)` : ''}
          </Button>
        </Space>

        <Space wrap size="middle" style={{ width: '100%' }}>
          <Select
            allowClear
            placeholder="Source"
            style={{ width: 140 }}
            value={ingestSource}
            onChange={v => setIngestSource(v as number | undefined)}
            options={SOURCE_OPTIONS}
          />
          <RangePicker
            value={uploadedRange as [Dayjs, Dayjs] | null}
            onChange={dates => {
              if (!dates) {
                setUploadedRange(null);
              } else {
                setUploadedRange([dates[0] ?? null, dates[1] ?? null]);
              }
            }}
            placeholder={['Uploaded from', 'Uploaded to']}
            allowEmpty={[true, true]}
          />
          <Space size={4}>
            <Text>Has video file</Text>
            <Switch checked={hasOrigin} onChange={setHasOrigin} />
          </Space>
          <Input.Search
            placeholder="Search title"
            allowClear
            style={{ width: 220 }}
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
          />
          <Select
            style={{ width: 180 }}
            value={ordering}
            onChange={setOrdering}
            options={ORDERING_OPTIONS}
          />
        </Space>

        {isFetching && rows.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 48 }}>
            <Spin />
          </div>
        ) : rows.length === 0 ? (
          <Empty description="No episodes match the filters" />
        ) : (
          <EpisodeDownloadTable
            rows={rows}
            loading={isFetching}
            pagination={{
              current: page,
              pageSize,
              total: totalCount,
              onChange: (p, ps) => {
                setPage(p);
                setPageSize(ps);
              },
              showSizeChanger: true,
              pageSizeOptions: PAGE_SIZE_OPTIONS,
              showTotal: (t, r) => `${r[0]}-${r[1]} of ${t}`,
            }}
          />
        )}
      </Space>
    </BasePageComponent>
  );
};
